import { useCallback, useMemo, useState } from "react";
import { containsOnlyNumbers, countValidNumbers, getTotalLength } from "@/utils/helper.util";
import { defaultHeadcountRanges, headCountRangeLabels } from "@/utils/ranges.util";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { HiOutlineMinus } from "react-icons/hi2";

import { SearchFilterItem } from "@/types/SearchFilterItem.type";
import InputError from "@/components/atoms/input/InputError";
import InputLabel from "@/components/atoms/input/InputLabel";
import FilterSectionItem from "@/components/molecules/filter/FilterSectionItem";
import FormInput from "@/components/molecules/form/FormInput";
import FormRangeCheckboxes from "@/components/molecules/form/FormRangeCheckboxes";

const HeadcountFilter = ({ name }: SearchFilterItem) => {
  const { t } = useTranslation();
  const { watch } = useFormContext();
  const [headcountFreeHasError, setHeadcountFreeHasError] = useState(false);

  const headcountFilter = watch("headcount");
  const headcountFreeFilter = watch("headcountFree");

  const validatePositiveNumber = useCallback((value: string): boolean => {
    const isValid = containsOnlyNumbers(value, true);
    setHeadcountFreeHasError(!isValid);
    return isValid;
  }, []);

  const headcountFreeLength = useMemo(
    () => countValidNumbers(headcountFreeFilter, true),
    [headcountFreeFilter.min, headcountFreeFilter.max],
  );

  return (
    <FilterSectionItem
      id={name}
      title={t("search.filter.headcount.title")}
      filterCount={getTotalLength(headcountFilter) + headcountFreeLength}
      tooltip={t("search.filter.headcount.tooltip")}
    >
      <FormRangeCheckboxes name="headcount" ranges={defaultHeadcountRanges} displayLabel={headCountRangeLabels} />

      <InputLabel label={t("search.filter.headcount.exact")} className={"!text-xs"} />
      <div className={"flex items-end gap-x-3"}>
        <FormInput
          name={"headcountFree.min"}
          label={t("search.filter.headcount.min")}
          placeholder={t("search.filter.headcount.min")}
          rules={{ validate: validatePositiveNumber }}
        />
        <HiOutlineMinus className={"my-3.5 h-4 w-4 shrink-0"} />
        <FormInput
          name={"headcountFree.max"}
          label={t("search.filter.headcount.max")}
          placeholder={t("search.filter.headcount.max")}
          rules={{ validate: validatePositiveNumber }}
        />
      </div>
      {headcountFreeHasError && <InputError error={t("validation.onlyNumbers")} />}
    </FilterSectionItem>
  );
};

export default HeadcountFilter;
